import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import { apiRequest, getImageUrl } from '../../utils/api';
import toast from 'react-hot-toast';

const StoriesPage = () => {
    const { organizationId } = useParams();
    const [stories, setStories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingStory, setEditingStory] = useState(null);
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);
    
    // Form state
    const [formData, setFormData] = useState({
        has_sound: true,
        link_url: '',
        link_type: '',
        link_target: '',
        link_text: 'Daha Fazla',
        start_date: '',
        end_date: '',
        start_time: '',
        end_time: '',
        active_weekdays: [],
        daily_schedule: {},
        display_duration: 5,
        is_active: true
    });
    const [mediaFile, setMediaFile] = useState(null);
    const [mediaPreview, setMediaPreview] = useState(null);

    useEffect(() => {
        loadStories();
    }, [organizationId]);

    const loadStories = async () => {
        try {
            const data = await apiRequest(`/organizations/${organizationId}/stories`);
            setStories(data.data || []);
        } catch (error) {
            console.error('Stories yüklenirken hata:', error);
            toast.error('Stories yüklenirken hata oluştu');
        } finally {
            setLoading(false);
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validate file type
        const isVideo = file.type.startsWith('video/');
        const isImage = file.type.startsWith('image/');
        
        if (!isVideo && !isImage) {
            toast.error('Sadece görsel veya video dosyası yükleyebilirsiniz');
            return;
        }

        // Max size: 50MB for video, 10MB for image
        const maxSize = isVideo ? 50 * 1024 * 1024 : 10 * 1024 * 1024;
        if (file.size > maxSize) {
            toast.error(`Dosya boyutu çok büyük. Max: ${isVideo ? '50MB' : '10MB'}`);
            return;
        }

        setMediaFile(file);
        
        // Preview
        const reader = new FileReader();
        reader.onloadend = () => {
            setMediaPreview({
                url: reader.result,
                type: isVideo ? 'video' : 'image'
            });
        };
        reader.readAsDataURL(file);
    };

    const resetForm = () => {
        setFormData({
            has_sound: true,
            link_url: '',
            link_type: '',
            link_target: '',
            link_text: 'Daha Fazla',
            start_date: '',
            end_date: '',
            start_time: '',
            end_time: '',
            active_weekdays: [],
            daily_schedule: {},
            display_duration: 5,
            is_active: true
        });
        setMediaFile(null);
        setMediaPreview(null);
        setEditingStory(null);
    };

    const openModal = (story = null) => {
        if (story) {
            setEditingStory(story);
            setFormData({
                has_sound: story.has_sound,
                link_url: story.link_url || '',
                link_type: story.link_type || '',
                link_target: story.link_target || '',
                link_text: story.link_text || 'Daha Fazla',
                start_date: story.start_date ? story.start_date.split('T')[0] : '',
                end_date: story.end_date ? story.end_date.split('T')[0] : '',
                start_time: story.start_time || '',
                end_time: story.end_time || '',
                active_weekdays: story.active_weekdays ? (Array.isArray(story.active_weekdays) ? story.active_weekdays : JSON.parse(story.active_weekdays)) : [],
                daily_schedule: story.daily_schedule ? (typeof story.daily_schedule === 'string' ? JSON.parse(story.daily_schedule) : story.daily_schedule) : {},
                display_duration: story.display_duration || 5,
                is_active: story.is_active
            });
            setMediaPreview({
                url: story.media_url,
                type: story.media_type
            });
        } else {
            resetForm();
        }
        setShowModal(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!editingStory && !mediaFile) {
            toast.error('Lütfen bir görsel veya video seçin');
            return;
        }

        setUploading(true);

        try {
            const formDataToSend = new FormData();
            
            if (mediaFile) {
                formDataToSend.append('media', mediaFile);
            }
            
            // Add all form fields - array/object'leri JSON.stringify ile gönder
            Object.entries(formData).forEach(([key, value]) => {
                if (value === '' || value === null || value === undefined) return;
                if (Array.isArray(value)) {
                    formDataToSend.append(key, JSON.stringify(value));
                } else if (typeof value === 'object') {
                    formDataToSend.append(key, JSON.stringify(value));
                } else {
                    formDataToSend.append(key, value);
                }
            });

            const url = editingStory 
                ? `/organizations/${organizationId}/stories/${editingStory.id}`
                : `/organizations/${organizationId}/stories`;
            
            const method = editingStory ? 'PUT' : 'POST';

            await apiRequest(url, {
                method,
                body: formDataToSend
            });

            toast.success(editingStory ? 'Story güncellendi!' : 'Story oluşturuldu!');
            setShowModal(false);
            resetForm();
            loadStories();
        } catch (error) {
            console.error('Story kaydetme hatası:', error);
            toast.error('Story kaydedilirken hata oluştu');
        } finally {
            setUploading(false);
        }
    };

    const handleDelete = async (storyId) => {
        if (!confirm('Bu story\'yi silmek istediğinize emin misiniz?')) return;

        try {
            await apiRequest(`/organizations/${organizationId}/stories/${storyId}`, {
                method: 'DELETE'
            });
            toast.success('Story silindi');
            loadStories();
        } catch (error) {
            toast.error('Story silinirken hata oluştu');
        }
    };

    const toggleActive = async (story) => {
        try {
            await apiRequest(`/organizations/${organizationId}/stories/${story.id}`, {
                method: 'PUT',
                body: JSON.stringify({ is_active: !story.is_active }),
                headers: { 'Content-Type': 'application/json' }
            });
            toast.success(story.is_active ? 'Story devre dışı bırakıldı' : 'Story aktif edildi');
            loadStories();
        } catch (error) {
            toast.error('Durum güncellenirken hata oluştu');
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString('tr-TR');
    };

    const linkTypes = [
        { value: '', label: 'Link Yok' },
        { value: 'internal', label: 'Menü İçi' },
        { value: 'category', label: 'Kategori' },
        { value: 'product', label: 'Ürün' },
        { value: 'external', label: 'Dış Link' },
        { value: 'whatsapp', label: 'WhatsApp' },
        { value: 'phone', label: 'Telefon' }
    ];

    return (
        <AdminLayout>
            <div className="space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Stories</h1>
                        <p className="text-gray-500 mt-1">Instagram benzeri story'ler ile müşterilerinize ulaşın</p>
                    </div>
                    <button
                        onClick={() => openModal()}
                        className="px-4 py-2 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-xl font-medium hover:from-orange-600 hover:to-red-600 transition flex items-center gap-2 shadow-lg"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Yeni Story
                    </button>
                </div>

                {/* Info Card */}
                <div className="bg-gradient-to-r from-orange-500 to-red-500 rounded-2xl p-6 text-white">
                    <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                        </div>
                        <div>
                            <h3 className="font-bold text-lg">Story Nasıl Çalışır?</h3>
                            <ul className="mt-2 space-y-1 text-white/90 text-sm">
                                <li>• Dikey format görsel veya video yükleyin (9:16 oran önerilir)</li>
                                <li>• Tarih/saat ayarları ile otomatik zamanlama yapın</li>
                                <li>• İç/dış link ekleyerek müşterileri yönlendirin</li>
                                <li>• Menü açıldığında story otomatik gösterilir (ilk ziyaret)</li>
                            </ul>
                        </div>
                    </div>
                </div>

                {/* Stories Grid */}
                {loading ? (
                    <div className="flex justify-center py-12">
                        <div className="animate-spin rounded-full h-12 w-12 border-4 border-orange-500 border-t-transparent"></div>
                    </div>
                ) : stories.length === 0 ? (
                    <div className="text-center py-12 bg-white rounded-2xl shadow">
                        <div className="w-20 h-20 mx-auto bg-orange-100 rounded-full flex items-center justify-center mb-4">
                            <svg className="w-10 h-10 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                        </div>
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">Henüz Story Yok</h3>
                        <p className="text-gray-500 mb-6">İlk story'nizi oluşturun ve müşterilerinize ulaşın</p>
                        <button
                            onClick={() => openModal()}
                            className="px-6 py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-xl font-medium hover:from-orange-600 hover:to-red-600 transition"
                        >
                            İlk Story'yi Oluştur
                        </button>
                    </div>
                ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                        {stories.map((story) => (
                            <div 
                                key={story.id}
                                className={`relative group rounded-2xl overflow-hidden shadow-lg ${
                                    !story.is_active ? 'opacity-60' : ''
                                }`}
                                style={{ aspectRatio: '9/16' }}
                            >
                                {/* Media */}
                                {story.media_type === 'video' ? (
                                    <video
                                        src={getImageUrl(story.media_url)}
                                        className="absolute inset-0 w-full h-full object-cover"
                                        muted
                                        loop
                                        onMouseEnter={(e) => e.target.play()}
                                        onMouseLeave={(e) => e.target.pause()}
                                        onError={(e) => {
                                            console.error('Story video load error:', e.target.src);
                                        }}
                                    />
                                ) : (
                                    <img
                                        src={getImageUrl(story.media_url)}
                                        alt="Story"
                                        className="absolute inset-0 w-full h-full object-cover"
                                        onError={(e) => {
                                            console.error('Story image load error:', e.target.src);
                                        }}
                                    />
                                )}

                                {/* Gradient Overlay */}
                                <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/60" />

                                {/* Status Badge */}
                                <div className="absolute top-2 left-2 flex gap-1">
                                    {story.is_active ? (
                                        <span className="px-2 py-0.5 bg-green-500 text-white text-xs rounded-full">
                                            Aktif
                                        </span>
                                    ) : (
                                        <span className="px-2 py-0.5 bg-gray-500 text-white text-xs rounded-full">
                                            Pasif
                                        </span>
                                    )}
                                    {story.media_type === 'video' && (
                                        <span className="px-2 py-0.5 bg-orange-500 text-white text-xs rounded-full flex items-center gap-1">
                                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                                                <path d="M8 5v14l11-7z" />
                                            </svg>
                                            Video
                                        </span>
                                    )}
                                </div>

                                {/* Stats */}
                                <div className="absolute top-2 right-2 text-white text-xs space-y-1">
                                    <div className="flex items-center gap-1 bg-black/40 px-2 py-0.5 rounded-full">
                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                        </svg>
                                        {story.stats?.views || 0}
                                    </div>
                                </div>

                                {/* Info */}
                                <div className="absolute bottom-0 left-0 right-0 p-3 text-white">
                                    {story.link_type && (
                                        <div className="text-xs mb-1 flex items-center gap-1">
                                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                                            </svg>
                                            {story.link_text}
                                        </div>
                                    )}
                                    <div className="text-xs text-white/70">
                                        {story.display_duration}s • {formatDate(story.start_date)} - {formatDate(story.end_date)}
                                    </div>
                                </div>

                                {/* Hover Actions */}
                                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition flex items-center justify-center gap-2">
                                    <button
                                        onClick={() => openModal(story)}
                                        className="p-3 bg-white rounded-xl text-gray-900 hover:bg-gray-100 transition"
                                        title="Düzenle"
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                        </svg>
                                    </button>
                                    <button
                                        onClick={() => toggleActive(story)}
                                        className={`p-3 rounded-xl transition ${
                                            story.is_active 
                                                ? 'bg-yellow-500 text-white hover:bg-yellow-600' 
                                                : 'bg-green-500 text-white hover:bg-green-600'
                                        }`}
                                        title={story.is_active ? 'Devre Dışı Bırak' : 'Aktif Et'}
                                    >
                                        {story.is_active ? (
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                        ) : (
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                        )}
                                    </button>
                                    <button
                                        onClick={() => handleDelete(story.id)}
                                        className="p-3 bg-red-500 rounded-xl text-white hover:bg-red-600 transition"
                                        title="Sil"
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Add/Edit Modal */}
            {showModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div 
                        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                        onClick={() => setShowModal(false)}
                    />
                    <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                        {/* Modal Header */}
                        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between z-10">
                            <h2 className="text-xl font-bold text-gray-900">
                                {editingStory ? 'Story Düzenle' : 'Yeni Story'}
                            </h2>
                            <button
                                onClick={() => setShowModal(false)}
                                className="p-2 text-gray-400 hover:text-gray-600 transition"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="p-6 space-y-6">
                            {/* Media Upload */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Görsel / Video *
                                </label>
                                <div 
                                    className="relative border-2 border-dashed border-gray-300 rounded-2xl overflow-hidden hover:border-orange-400 transition cursor-pointer"
                                    style={{ aspectRatio: '9/16', maxHeight: '400px' }}
                                    onClick={() => fileInputRef.current?.click()}
                                >
                                    {mediaPreview ? (
                                        <>
                                            {mediaPreview.type === 'video' ? (
                                                <video
                                                    src={mediaPreview.url}
                                                    className="absolute inset-0 w-full h-full object-cover"
                                                    autoPlay
                                                    muted
                                                    loop
                                                />
                                            ) : (
                                                <img
                                                    src={mediaPreview.url}
                                                    alt="Preview"
                                                    className="absolute inset-0 w-full h-full object-cover"
                                                />
                                            )}
                                            <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 hover:opacity-100 transition">
                                                <span className="text-white font-medium">Değiştirmek için tıkla</span>
                                            </div>
                                        </>
                                    ) : (
                                        <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-400">
                                            <svg className="w-16 h-16 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                            </svg>
                                            <p className="text-lg font-medium">Görsel veya video yükle</p>
                                            <p className="text-sm text-gray-400 mt-1">9:16 oran önerilir</p>
                                            <p className="text-xs text-gray-400 mt-1">Görsel: max 10MB, Video: max 50MB</p>
                                        </div>
                                    )}
                                </div>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*,video/*"
                                    onChange={handleFileChange}
                                    className="hidden"
                                />
                            </div>

                            {/* Video Settings */}
                            {mediaPreview?.type === 'video' && (
                                <div className="flex items-center justify-between p-4 bg-orange-50 rounded-xl">
                                    <div>
                                        <span className="font-medium text-gray-900">Ses</span>
                                        <p className="text-sm text-gray-500">Video açıldığında ses çalsın mı?</p>
                                    </div>
                                    <label className="relative inline-flex items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={formData.has_sound}
                                            onChange={(e) => setFormData(prev => ({ ...prev, has_sound: e.target.checked }))}
                                            className="sr-only peer"
                                        />
                                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-500"></div>
                                    </label>
                                </div>
                            )}

                            {/* Display Duration */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Görüntülenme Süresi (saniye)
                                </label>
                                <input
                                    type="number"
                                    min="3"
                                    max="30"
                                    value={formData.display_duration}
                                    onChange={(e) => setFormData(prev => ({ ...prev, display_duration: parseInt(e.target.value) || 5 }))}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                                />
                                <p className="text-xs text-gray-500 mt-1">Görsel için: 3-30 saniye. Video için süre otomatik</p>
                            </div>

                            {/* Link Settings */}
                            <div className="space-y-4 p-4 bg-gray-50 rounded-xl">
                                <h3 className="font-medium text-gray-900">Link Ayarları</h3>
                                
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Link Türü
                                        </label>
                                        <select
                                            value={formData.link_type}
                                            onChange={(e) => setFormData(prev => ({ ...prev, link_type: e.target.value }))}
                                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                                        >
                                            {linkTypes.map(type => (
                                                <option key={type.value} value={type.value}>
                                                    {type.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    
                                    {formData.link_type && (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Buton Metni
                                            </label>
                                            <input
                                                type="text"
                                                value={formData.link_text}
                                                onChange={(e) => setFormData(prev => ({ ...prev, link_text: e.target.value }))}
                                                placeholder="Daha Fazla"
                                                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                                            />
                                        </div>
                                    )}
                                </div>

                                {formData.link_type && formData.link_type !== '' && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            {formData.link_type === 'external' ? 'URL' : 
                                             formData.link_type === 'category' ? 'Kategori ID' :
                                             formData.link_type === 'product' ? 'Ürün ID' :
                                             formData.link_type === 'whatsapp' ? 'WhatsApp Numara' :
                                             formData.link_type === 'phone' ? 'Telefon Numara' : 'Hedef'}
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.link_url}
                                            onChange={(e) => setFormData(prev => ({ ...prev, link_url: e.target.value }))}
                                            placeholder={
                                                formData.link_type === 'external' ? 'https://...' :
                                                formData.link_type === 'whatsapp' ? '+90...' :
                                                formData.link_type === 'phone' ? '+90...' : 'ID veya değer'
                                            }
                                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                                        />
                                    </div>
                                )}
                            </div>

                            {/* Schedule Settings */}
                            <div className="space-y-4 p-4 bg-gray-50 rounded-xl">
                                <h3 className="font-medium text-gray-900">Zamanlama</h3>
                                
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Başlangıç Tarihi
                                        </label>
                                        <input
                                            type="date"
                                            value={formData.start_date}
                                            onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
                                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Bitiş Tarihi
                                        </label>
                                        <input
                                            type="date"
                                            value={formData.end_date}
                                            onChange={(e) => setFormData(prev => ({ ...prev, end_date: e.target.value }))}
                                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Günlük Başlangıç Saati
                                        </label>
                                        <input
                                            type="time"
                                            value={formData.start_time}
                                            onChange={(e) => setFormData(prev => ({ ...prev, start_time: e.target.value }))}
                                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Günlük Bitiş Saati
                                        </label>
                                        <input
                                            type="time"
                                            value={formData.end_time}
                                            onChange={(e) => setFormData(prev => ({ ...prev, end_time: e.target.value }))}
                                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                                        />
                                    </div>
                                </div>
                                <p className="text-xs text-gray-500">Boş bırakırsanız süresiz/tüm gün aktif olur</p>
                                
                                {/* Active Weekdays */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Aktif Günler (Boş = Tüm Günler)
                                    </label>
                                    <div className="grid grid-cols-4 gap-2">
                                        {[
                                            { value: 'monday', label: 'Pazartesi' },
                                            { value: 'tuesday', label: 'Salı' },
                                            { value: 'wednesday', label: 'Çarşamba' },
                                            { value: 'thursday', label: 'Perşembe' },
                                            { value: 'friday', label: 'Cuma' },
                                            { value: 'saturday', label: 'Cumartesi' },
                                            { value: 'sunday', label: 'Pazar' }
                                        ].map(day => (
                                            <label key={day.value} className="flex items-center space-x-2 cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    checked={formData.active_weekdays.includes(day.value)}
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            setFormData(prev => ({
                                                                ...prev,
                                                                active_weekdays: [...prev.active_weekdays, day.value]
                                                            }));
                                                        } else {
                                                            setFormData(prev => ({
                                                                ...prev,
                                                                active_weekdays: prev.active_weekdays.filter(d => d !== day.value),
                                                                daily_schedule: Object.fromEntries(
                                                                    Object.entries(prev.daily_schedule).filter(([k]) => k !== day.value)
                                                                )
                                                            }));
                                                        }
                                                    }}
                                                    className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                                                />
                                                <span className="text-sm text-gray-700">{day.label}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                                
                                {/* Daily Schedule */}
                                {formData.active_weekdays.length > 0 && (
                                    <div className="space-y-3">
                                        <label className="block text-sm font-medium text-gray-700">
                                            Günlük Saat Aralıkları (Her gün için farklı saatler belirleyebilirsiniz)
                                        </label>
                                        {formData.active_weekdays.map(day => {
                                            const daySchedule = formData.daily_schedule[day] || { start: '', end: '', active: true };
                                            return (
                                                <div key={day} className="p-3 bg-white rounded-lg border border-gray-200">
                                                    <div className="flex items-center justify-between mb-2">
                                                        <span className="font-medium text-gray-700 capitalize">
                                                            {day === 'monday' ? 'Pazartesi' :
                                                             day === 'tuesday' ? 'Salı' :
                                                             day === 'wednesday' ? 'Çarşamba' :
                                                             day === 'thursday' ? 'Perşembe' :
                                                             day === 'friday' ? 'Cuma' :
                                                             day === 'saturday' ? 'Cumartesi' : 'Pazar'}
                                                        </span>
                                                        <label className="flex items-center space-x-2 cursor-pointer">
                                                            <input
                                                                type="checkbox"
                                                                checked={daySchedule.active}
                                                                onChange={(e) => {
                                                                    setFormData(prev => ({
                                                                        ...prev,
                                                                        daily_schedule: {
                                                                            ...prev.daily_schedule,
                                                                            [day]: { ...daySchedule, active: e.target.checked }
                                                                        }
                                                                    }));
                                                                }}
                                                                className="w-4 h-4 text-orange-600 border-gray-300 rounded"
                                                            />
                                                            <span className="text-xs text-gray-600">Aktif</span>
                                                        </label>
                                                    </div>
                                                    {daySchedule.active && (
                                                        <div className="grid grid-cols-2 gap-2">
                                                            <input
                                                                type="time"
                                                                value={daySchedule.start || ''}
                                                                onChange={(e) => {
                                                                    setFormData(prev => ({
                                                                        ...prev,
                                                                        daily_schedule: {
                                                                            ...prev.daily_schedule,
                                                                            [day]: { ...daySchedule, start: e.target.value }
                                                                        }
                                                                    }));
                                                                }}
                                                                placeholder="Başlangıç"
                                                                className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                                            />
                                                            <input
                                                                type="time"
                                                                value={daySchedule.end || ''}
                                                                onChange={(e) => {
                                                                    setFormData(prev => ({
                                                                        ...prev,
                                                                        daily_schedule: {
                                                                            ...prev.daily_schedule,
                                                                            [day]: { ...daySchedule, end: e.target.value }
                                                                        }
                                                                    }));
                                                                }}
                                                                placeholder="Bitiş"
                                                                className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                        <p className="text-xs text-gray-500">Boş bırakırsanız o gün için tüm gün aktif olur</p>
                                    </div>
                                )}
                            </div>

                            {/* Active Toggle */}
                            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                                <div>
                                    <span className="font-medium text-gray-900">Aktif</span>
                                    <p className="text-sm text-gray-500">Story yayında olsun mu?</p>
                                </div>
                                <label className="relative inline-flex items-center cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={formData.is_active}
                                        onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                                        className="sr-only peer"
                                    />
                                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-500"></div>
                                </label>
                            </div>

                            {/* Submit Button */}
                            <div className="flex gap-3 pt-4 border-t">
                                <button
                                    type="button"
                                    onClick={() => setShowModal(false)}
                                    className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition"
                                >
                                    İptal
                                </button>
                                <button
                                    type="submit"
                                    disabled={uploading}
                                    className="flex-1 px-6 py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-xl font-medium hover:from-orange-600 hover:to-red-600 disabled:opacity-50 transition flex items-center justify-center gap-2"
                                >
                                    {uploading ? (
                                        <>
                                            <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                                            </svg>
                                            Yükleniyor...
                                        </>
                                    ) : editingStory ? 'Güncelle' : 'Oluştur'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </AdminLayout>
    );
};

export default StoriesPage;

